import { Close, Edit } from '@mui/icons-material';
import { Box, Button, Theme, Typography, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import { useEffect, useMemo, useState, FC, useContext } from 'react';
import { MobileTable, Pagination, Table } from '../../../components';
import { getUnifiedUsers, getUserJobTitles } from '../../../fetch';
import { IUnifiedUser, IJobTitle } from '../../../models';
import { UsersListFilters } from './users-list-filters';
import { logError } from '../../../services';
import { GlobalContext } from '../../../context/global';
import { updateFilters, updateSort } from '../../../context/actions';
import { getEnvironment } from '../../../helpers';

interface IUsersList {}

const PAGE_NAME = 'UUM-user-list';

export const UsersList: FC<IUsersList> = () => {
  const classes = useStyles();
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [users, setUsers] = useState<IUnifiedUser[]>([]);
  const [recordCount, setRecordCount] = useState<number>(0);
  const isMobile = useMediaQuery('(max-width: 1100px)');

  const { enqueueSnackbar } = useSnackbar();
  const [jobTitles, setJobTitles] = useState<IJobTitle[]>([]);
  const [isLoadingJobTitles, setLoadingJobTitles] = useState<boolean>(true);

  const { filterValues, dispatchFilters, sortingValues, dispatchSorting } =
    useContext(GlobalContext);

  const sort = useMemo<{
    sortBy: string;
    sortDirection: { [key: string]: 'Asc' | 'Desc' };
  }>(() => {
    if (sortingValues) {
      const sValues = sortingValues.find(s => s.pageName === PAGE_NAME);
      if (sValues) {
        return {
          sortBy: sValues?.sort ?? 'Name',
          sortDirection: {
            [sValues.sort]: (sValues?.sortDirection?.[sValues.sort] as 'Asc' | 'Desc') ?? 'Asc'
          }
        };
      }
      return {
        sortBy: 'Name',
        sortDirection: {
          Name: 'Asc'
        }
      };
    }
  }, [sortingValues]);

  const filters = useMemo<{
    search: string;
    jobTitle: string;
    page: number;
    perPage: number;
  }>(() => {
    if (filterValues) {
      const fValues = filterValues.find(f => f.pageName === PAGE_NAME);
      if (fValues) {
        return {
          search: fValues?.filters?.search ?? '',
          jobTitle: fValues?.filters?.jobTitle ?? '',
          page: fValues?.filters?.page ?? 0,
          perPage: fValues?.filters?.perPage ?? 10
        };
      }
      return {
        search: '',
        jobTitle: '',
        page: 0,
        perPage: 10
      };
    }
  }, [filterValues]);

  const setFilters = (filters: {
    search: string;
    jobTitle: string;
    page: number;
    perPage: number;
  }) => {
    dispatchFilters(
      updateFilters({
        pageName: PAGE_NAME,
        environment: getEnvironment(),
        filters
      })
    );
  };

  const [searchValue, setSearch] = useState<string>(filters.search ?? '');
  const [searchedValue, setSearchedValue] = useState<string>(filters.search ?? '');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(
    Object.values({
      search: filters.search,
      jobTitle: filters.jobTitle
    }).filter(Boolean).length > 0 || false
  );
  const [selectedJobTitle, setSelectedJobTitle] = useState<string | number>(filters.jobTitle ?? '');

  const fetchJobTitles = async () => {
    try {
      setLoadingJobTitles(true);
      const jobTitles = await getUserJobTitles();
      setJobTitles(jobTitles);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Failed to fetch job titles. Please try again.', {
        variant: 'error'
      });
      logError(error, 'fetchJobTitles failed');
    } finally {
      setLoadingJobTitles(false);
    }
  };

  const fetchUsers = async (clearSearch?: boolean) => {
    setLoading(true);
    try {
      const users = await getUnifiedUsers({
        page: filters.page + 1,
        perPage: filters.perPage,
        sortBy: sort.sortBy,
        sortDirection: sort.sortDirection[sort.sortBy],
        search: clearSearch ? undefined : searchValue,
        jobTitle: filters?.jobTitle
      });
      if (!clearSearch && searchValue) {
        setSearchedValue(searchValue);
      } else if (clearSearch) {
        setSearch('');
        setSearchedValue('');
      }
      setRecordCount(users.totalRecordCount);
      setUsers(users?.records);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Failed to fetch users. Please try again.', {
        variant: 'error'
      });
      logError(error, 'fetchUsers failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClickColumn = (column: string) => {
    dispatchSorting(
      updateSort({
        sort: column,
        sortDirection: {
          [column]: sort?.sortDirection?.[column] === 'Asc' ? 'Desc' : 'Asc'
        },
        pageName: PAGE_NAME,
        environment: getEnvironment()
      })
    );
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: sort?.sortBy === 'Name',
        isServerSortedDesc: sort?.sortDirection?.[sort.sortBy] === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Email',
        accessor: 'email',
        isServerSorted: sort?.sortBy === 'Email',
        isServerSortedDesc: sort?.sortDirection?.[sort.sortBy] === 'Desc',
        handleClickColumn: () => handleClickColumn('Email'),
        Cell: props => <span>{props.cell.row.original?.email ?? ''}</span>
      },
      {
        Header: 'Job Title',
        accessor: 'jobTitle',
        isServerSorted: sort?.sortBy === 'JobTitle',
        isServerSortedDesc: sort?.sortDirection?.[sort.sortBy] === 'Desc',
        handleClickColumn: () => handleClickColumn('JobTitle')
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IUnifiedUser } };
        }) => {
          return (
            original.userId && (
              <Box display="flex">
                <Button
                  id="edit"
                  color="primary"
                  startIcon={<Edit />}
                  sx={{ textAlign: 'left' }}
                  onClick={() => {
                    handleEdit(original);
                  }}
                >
                  Edit
                </Button>
              </Box>
            )
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const handleEdit = async (user: IUnifiedUser) => {
    history.push(`/admin/user-tools/unified-user-management/${user.userId}`);
  };

  useEffect(() => {
    fetchJobTitles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, filters]);

  return (
    <>
      <Box mt={1}>
        <UsersListFilters
          isLoading={isLoading}
          setHasAppliedFilters={setHasAppliedFilters}
          hasAppliedFilters={hasAppliedFilters}
          setSearchValue={setSearch}
          setSearchedValue={setSearchedValue}
          searchValue={searchValue}
          handleSearch={(val: string) => {
            setSearchedValue(val);
            setFilters({
              search: val,
              jobTitle: filters?.jobTitle,
              page: 0,
              perPage: filters.perPage
            });
          }}
          setSelectedJobTitle={setSelectedJobTitle}
          selectedJobTitle={selectedJobTitle}
          jobTitles={jobTitles}
          isLoadingJobTitles={isLoadingJobTitles}
          applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
            if (clearFilters) {
              setFilters({
                search: '',
                jobTitle: '',
                page: filters.page,
                perPage: filters.perPage
              });
            } else {
              setFilters({
                ...filters,
                search: searchValue,
                jobTitle: selectedJobTitle ? `${selectedJobTitle}` : '',
                page: 0,
                perPage: filters.perPage
              });
            }
          }}
        />
      </Box>
      {searchedValue && (
        <Box marginTop="1rem" display="flex" alignItems="center">
          <Typography color="secondary">Showing results for "{searchedValue}"</Typography>
          <Button
            id="clear-search"
            size="small"
            startIcon={<Close />}
            className={classes.clearSearchButton}
            onClick={() => {
              setSearch('');
              setSearchedValue('');
              fetchUsers(true);
            }}
          >
            Clear Search
          </Button>
        </Box>
      )}
      <Box marginTop="1rem">
        <Table
          columns={columns}
          data={users}
          isLoading={isLoading}
          hidePagination
          mobileBreakPoint={1100}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
          mobileProps={{
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              })),
            handleEdit: (val: IUnifiedUser) => handleEdit(val),
            showEdit: (val: IUnifiedUser) => !!val.userId
          }}
        />
        {!isLoading && (
          <Pagination
            page={filters.page}
            count={recordCount}
            rowsPerPage={filters.perPage}
            setPage={(page: number) => {
              setFilters({
                ...filters,
                page
              });
            }}
            setRowsPerPage={(perPage: number) => {
              setFilters({
                ...filters,
                page: 0,
                perPage
              });
            }}
          />
        )}
      </Box>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  clearSearchButton: {
    '@media (min-width: 408px)': {
      marginTop: 3,
      marginLeft: theme.spacing(1)
    }
  }
}));
